import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Upload, Download, Trash2, Eye, ChevronDown, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import CvCrossref from "./CvCrossref";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

const STROKE = 1.5;
const ACCEPT = ".pdf,.docx,.doc";
const ALLOWED = [".pdf", ".docx", ".doc"];

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function SearchableBadge({ status }) {
  if (status === "extracted") {
    return <span className="inline-flex items-center rounded border border-emerald-800/20 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">Searchable</span>;
  }
  return <span className="inline-flex items-center rounded border border-ink-200 bg-ink-50 px-1.5 py-0.5 text-[10px] font-medium text-ink-500" title="No extractable text — stored and downloadable, but can't be cross-referenced.">Not searchable</span>;
}

// Phase 4f — candidate résumé/CV: upload/replace (recruiter+), inline PDF
// preview, previous-version history, admin delete, + the factual CV↔interview
// cross-reference (Phase 4f.1).
export default function CandidateCV({ candidateId }) {
  const { member } = useAuth();
  const role = member?.role;
  const canUpload = role === "admin" || role === "recruiter";
  const canDelete = role === "admin";

  const [cvs, setCvs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/candidates/${candidateId}/cvs`);
      setCvs(res.data);
    } catch {
      setCvs({ active: null, previous: [] });
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ALLOWED.some((x) => lower.endsWith(x))) {
      toast.error("Upload a PDF, DOCX, or DOC file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10MB.");
      return;
    }
    setUploading(true);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/candidates/${candidateId}/cv`, fd, { headers: { "Content-Type": undefined } });
      toast.success("CV uploaded.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const fetchBlob = async (cv) => {
    const res = await api.get(`/candidates/${candidateId}/cv/${cv.cv_id}/file`, { responseType: "blob" });
    return new Blob([res.data], { type: cv.content_type });
  };

  const onDownload = async (cv) => {
    try {
      const blob = await fetchBlob(cv);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cv.filename || "cv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error("Couldn't download the file.");
    }
  };

  const onPreview = async (cv) => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); return; }
    try {
      const blob = await fetchBlob(cv);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      toast.error("Couldn't open the preview.");
    }
  };

  const confirmDelete = async () => {
    const cv = deleteTarget;
    setDeleteTarget(null);
    if (!cv) return;
    try {
      await api.delete(`/candidates/${candidateId}/cv/${cv.cv_id}`);
      toast("CV deleted.");
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const active = cvs?.active;
  const previous = cvs?.previous || [];

  return (
    <section data-testid="candidate-cv-section">
      <div className="hp-section-label mb-3">Résumé / CV</div>
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid="cv-file-input" />

      {loading ? (
        <div className="hp-card flex justify-center p-8"><Loader2 className="h-4 w-4 animate-spin text-ink-400" strokeWidth={STROKE} /></div>
      ) : !active ? (
        <div className="hp-card flex flex-col items-center justify-center p-8 text-center" data-testid="cv-empty">
          <FileText className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
          <p className="mt-3 text-[14px] text-ink-500">No résumé on file.</p>
          {canUpload ? (
            <button onClick={onPick} disabled={uploading} className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white transition hover:bg-emerald-900 disabled:opacity-60" data-testid="cv-upload-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Upload className="h-4 w-4" strokeWidth={STROKE} />}
              {uploading ? "Uploading…" : "Upload CV"}
            </button>
          ) : (
            <p className="mt-2 text-[12px] text-ink-400">A recruiter can add one.</p>
          )}
        </div>
      ) : (
        <div className="hp-card p-5" data-testid="cv-active">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="rounded-md border border-ink-200 bg-ink-50 p-2"><FileText className="h-5 w-5 text-emerald-800" strokeWidth={STROKE} /></div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="truncate text-[14px] font-medium text-ink-900" data-testid="cv-active-filename">{active.filename}</span>
                  <SearchableBadge status={active.extraction_status} />
                </div>
                <div className="mt-0.5 text-[12px] text-ink-500">
                  {fmtSize(active.size_bytes)}
                  {active.page_count ? ` · ${active.page_count} page${active.page_count === 1 ? "" : "s"}` : ""}
                  {active.uploaded_by_name ? ` · ${active.uploaded_by_name}` : ""}
                  {active.uploaded_at ? ` · ${new Date(active.uploaded_at).toLocaleDateString()}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {active.content_type === "application/pdf" && (
                <button onClick={() => onPreview(active)} className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-[12px] font-medium text-ink-600 transition hover:border-emerald-800/40 hover:text-emerald-900" data-testid="cv-preview-button">
                  {previewUrl ? <X className="h-3.5 w-3.5" strokeWidth={STROKE} /> : <Eye className="h-3.5 w-3.5" strokeWidth={STROKE} />}
                  {previewUrl ? "Hide" : "Preview"}
                </button>
              )}
              <button onClick={() => onDownload(active)} className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-[12px] font-medium text-ink-600 transition hover:border-emerald-800/40 hover:text-emerald-900" data-testid="cv-download-button">
                <Download className="h-3.5 w-3.5" strokeWidth={STROKE} /> Download
              </button>
              {canUpload && (
                <button onClick={onPick} disabled={uploading} className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-[12px] font-medium text-ink-600 transition hover:border-emerald-800/40 hover:text-emerald-900 disabled:opacity-60" data-testid="cv-replace-button">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={STROKE} /> : <Upload className="h-3.5 w-3.5" strokeWidth={STROKE} />}
                  Replace
                </button>
              )}
              {canDelete && (
                <button onClick={() => setDeleteTarget(active)} className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-[12px] font-medium text-ink-500 transition hover:border-red-300 hover:text-red-600" data-testid="cv-delete-button">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={STROKE} />
                </button>
              )}
            </div>
          </div>

          {previewUrl && (
            <iframe title="CV preview" src={previewUrl} className="mt-4 h-[600px] w-full rounded-md border border-ink-200" data-testid="cv-preview-frame" />
          )}

          {previous.length > 0 && (
            <div className="mt-4 border-t border-ink-100 pt-3">
              <button onClick={() => setShowPrevious((v) => !v)} className="inline-flex items-center gap-1 text-[12px] font-medium text-ink-500 hover:text-ink-800" data-testid="cv-previous-toggle">
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showPrevious ? "rotate-180" : ""}`} strokeWidth={STROKE} />
                Previous versions ({previous.length})
              </button>
              {showPrevious && (
                <ul className="mt-2 space-y-1.5" data-testid="cv-previous-list">
                  {previous.map((cv) => (
                    <li key={cv.cv_id} className="flex items-center justify-between gap-2 rounded-md border border-ink-100 bg-ink-50/50 px-3 py-1.5" data-testid={`cv-previous-${cv.cv_id}`}>
                      <div className="min-w-0 text-[12px] text-ink-600">
                        <span className="truncate">{cv.filename}</span>
                        <span className="text-ink-400"> · {cv.archived_at ? `archived ${new Date(cv.archived_at).toLocaleDateString()}` : "archived"}</span>
                      </div>
                      <button onClick={() => onDownload(cv)} className="inline-flex items-center gap-1 text-[12px] text-ink-500 hover:text-emerald-900" data-testid={`cv-previous-download-${cv.cv_id}`}>
                        <Download className="h-3.5 w-3.5" strokeWidth={STROKE} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="mt-5 border-t border-ink-100 pt-4">
            <CvCrossref key={active.cv_id} candidateId={candidateId} canGenerate={canUpload} />
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="cv-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-red-600" strokeWidth={STROKE} /> Delete this résumé?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{deleteTarget?.filename}</strong> and its cross-reference from this candidate&rsquo;s record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] text-ink-600">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={STROKE} />
            The deletion is recorded in the audit log.
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cv-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="cv-delete-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
